import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import GpsFixedOutlinedIcon from '@mui/icons-material/GpsFixedOutlined';
import { useSnackbar } from 'notistack';
import Tracking from '@/shared/services/tracking';
import { FramingPreset } from '../types';
import { useDeleteFramingPresetMutation, useFramingPresetsQuery } from '../hooks/useFramingPresets';

interface FramingPresetMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Camera in preview; presets are aimed from it. */
  cameraId?: number;
  /** Preset the clip is currently framed with. */
  activePresetId?: number | null;
  /** The clip is being saved. */
  busy: boolean;
  /** Frames the clip with a saved preset. */
  onApplyPreset: (preset: FramingPreset) => void;
  /** Opens target mode: the section under the player that aims at a spot. */
  onAimAtSpot: () => void;
  onUseCurrent: () => void;
}

const sectionLabelSx = {
  fontSize: '10px',
  lineHeight: '14px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  px: 1.5,
  pt: 1.25,
  pb: 0.5,
} as const;

/**
 * The "Set / Update Position" dropdown: apply a saved preset in one click,
 * open target mode to aim at a new spot (and save it there), or use the frame
 * as is. A preset is a spot in the arena, so it frames the same thing from
 * every camera.
 */
export const FramingPresetMenu: React.FC<FramingPresetMenuProps> = ({
  anchorEl,
  onClose,
  cameraId,
  activePresetId,
  busy,
  onApplyPreset,
  onAimAtSpot,
  onUseCurrent,
}) => {
  const { cx } = useTheme().palette;
  const { enqueueSnackbar } = useSnackbar();
  const presetsQuery = useFramingPresetsQuery(cameraId);
  const deletePreset = useDeleteFramingPresetMutation();
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const presets = presetsQuery.data ?? [];
  const working = busy || deletePreset.isPending;

  const close = () => {
    setConfirmingDelete(null);
    onClose();
  };

  const apply = (preset: FramingPreset) => {
    Tracking.getInstance().track('Apply Framing Preset', { category: 'Clips', source: 'Menu' });
    onApplyPreset(preset);
    close();
  };

  const remove = async (preset: FramingPreset) => {
    setConfirmingDelete(null);
    try {
      await deletePreset.mutateAsync(preset.uniqueId);
      Tracking.getInstance().track('Delete Framing Preset', { category: 'Clips' });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || error.message, { variant: 'error' });
    }
  };

  const row = (preset: FramingPreset) => {
    const active = preset.id === activePresetId;
    const confirming = confirmingDelete === preset.uniqueId;
    return (
      <Stack
        key={preset.uniqueId}
        direction="row"
        role="menuitemradio"
        aria-checked={active}
        tabIndex={0}
        data-testid={`framing-preset-${preset.uniqueId}`}
        onClick={() => !working && !confirming && apply(preset)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !working && !confirming) {
            e.preventDefault();
            apply(preset);
          }
        }}
        onMouseLeave={() => confirming && setConfirmingDelete(null)}
        sx={{
          alignItems: 'center',
          minHeight: 32,
          px: 1.5,
          gap: 1,
          cursor: working ? 'default' : 'pointer',
          outline: 'none',
          backgroundColor: active ? cx.bg.elevated2 : 'transparent',
          '&:hover, &:focus-visible': { backgroundColor: cx.bg.elevated2 },
          '&:hover .preset-delete, &:focus-within .preset-delete': { opacity: 1 },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            border: `1px solid ${active ? cx.blue.hover : cx.border.default}`,
            backgroundColor: active ? cx.blue.hover : 'transparent',
          }}
        />
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '12px',
            lineHeight: '16px',
            color: active ? cx.blue.hover : cx.text.primary,
          }}
        >
          {preset.name}
        </Typography>
        {confirming ? (
          <Button
            size="small"
            disabled={working}
            onClick={(e) => {
              e.stopPropagation();
              remove(preset);
            }}
            sx={{
              minWidth: 0,
              height: 22,
              px: 1,
              fontSize: '11px',
              textTransform: 'none',
              color: cx.text.primary,
              backgroundColor: cx.red.main,
              '&:hover': { backgroundColor: cx.red.deep },
            }}
          >
            Delete
          </Button>
        ) : (
          <Tooltip title="Delete preset" placement="left">
            <IconButton
              className="preset-delete"
              size="small"
              aria-label={`Delete preset ${preset.name}`}
              disabled={working}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmingDelete(preset.uniqueId);
              }}
              sx={{ p: 0.25, opacity: active ? 1 : 0, color: cx.text.tertiary }}
            >
              <DeleteOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            width: 260,
            mt: 0.5,
            backgroundColor: cx.bg.elevated,
            border: `1px solid ${cx.border.subtle}`,
            borderRadius: 1,
          },
        },
      }}
    >
      <Typography sx={{ ...sectionLabelSx, color: cx.text.tertiary }}>Saved presets</Typography>
      <Box role="menu" aria-label="Saved presets" sx={{ pb: 0.5 }}>
        {presetsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={14} />
          </Box>
        ) : presets.length ? (
          presets.map(row)
        ) : (
          <Typography
            sx={{ fontSize: '12px', lineHeight: '16px', color: cx.text.muted, px: 1.5, py: 0.5 }}
          >
            No saved presets yet
          </Typography>
        )}
      </Box>

      <Box sx={{ borderTop: `1px solid ${cx.border.subtle}` }}>
        <Button
          fullWidth
          disabled={working}
          startIcon={<GpsFixedOutlinedIcon sx={{ color: cx.gold }} />}
          onClick={() => {
            close();
            onAimAtSpot();
          }}
          sx={{
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            px: 1.5,
            py: 1,
            textTransform: 'none',
            textAlign: 'left',
            '& .MuiButton-startIcon': { mt: '1px' },
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '12px', lineHeight: '16px', color: cx.text.primary }}>
              Aim at a spot on the rink
            </Typography>
            <Typography sx={{ fontSize: '11px', lineHeight: '15px', color: cx.text.tertiary }}>
              Pick it on the rink, raise it if needed, save it as a preset
            </Typography>
          </Box>
        </Button>
      </Box>

      <Box sx={{ borderTop: `1px solid ${cx.border.subtle}` }}>
        <Button
          fullWidth
          disabled={working}
          onClick={() => {
            close();
            onUseCurrent();
          }}
          sx={{
            justifyContent: 'flex-start',
            px: 1.5,
            py: 1,
            fontSize: '12px',
            textTransform: 'none',
            color: cx.text.secondary,
          }}
        >
          Use this frame without saving
        </Button>
      </Box>
    </Popover>
  );
};

export default FramingPresetMenu;
